import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { floatRechargeApi } from '../api/float-recharge.api';
import type {
  CreateRechargePayload,
  RechargeStatus,
} from '../types/float-recharge.types';

// ─── Agent hook ───────────────────────────────────────────────────────────────

export function useFloatRecharge(statusFilter?: RechargeStatus) {
  const qc = useQueryClient();

  const requestsQuery = useQuery({
    queryKey: ['float-recharge-requests', statusFilter ?? 'all'],
    queryFn: () => floatRechargeApi.getMyRequests(statusFilter),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateRechargePayload) =>
      floatRechargeApi.requestRecharge(payload),
    onSuccess: () => {
      toast.success('Solicitud enviada correctamente');
      qc.invalidateQueries({ queryKey: ['float-recharge-requests'] });
      qc.invalidateQueries({ queryKey: ['agentMeProfile'] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Error al enviar la solicitud';
      toast.error(msg);
    },
  });

  return {
    requests: requestsQuery.data ?? [],
    isLoading: requestsQuery.isLoading,
    isError: requestsQuery.isError,
    createMutation,
  };
}

// ─── Admin hook ───────────────────────────────────────────────────────────────

export function useFloatRechargeAdmin(params?: {
  status?: RechargeStatus;
  agentId?: string;
  page?: number;
  limit?: number;
}) {
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['admin-float-recharge-requests', params],
    queryFn: () => floatRechargeApi.adminListRequests(params),
    staleTime: 15_000,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin-float-recharge-requests'] });

  const approveMutation = useMutation({
    mutationFn: (requestId: string) =>
      floatRechargeApi.adminApprove(requestId),
    onSuccess: () => {
      toast.success('Solicitud aprobada. Float acreditado.');
      invalidate();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Error al aprobar';
      toast.error(msg);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      floatRechargeApi.adminReject(requestId, reason),
    onSuccess: () => {
      toast.success('Solicitud rechazada.');
      invalidate();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Error al rechazar';
      toast.error(msg);
    },
  });

  return {
    data: listQuery.data,
    requests: listQuery.data?.data ?? [],
    meta: listQuery.data?.meta,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    approveMutation,
    rejectMutation,
  };
}
